import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api, formatApiError, normalizeLangCode } from "../lib/api";
import { Send, Download, Trash2, FileText, Paperclip, X, Search, CalendarClock } from "lucide-react";

const LANG_OPTS = [
  ["ro", "🇷🇴 Română"], ["en", "🇬🇧 English"], ["fr", "🇫🇷 Français"], ["de", "🇩🇪 Deutsch"],
  ["it", "🇮🇹 Italiano"], ["es", "🇪🇸 Español"], ["pt", "🇵🇹 Português"],
  ["pt-BR", "🇧🇷 Português (Brasil)"], ["sr", "🇷🇸 Srpski"],
];

const STATUS_COLORS = { sent: "#9a7530", submitted: "#0d7a3c", generated: "#0d1b2a" };

const fmtDate = (rec) => (rec.submitted_at || rec.created_at || "").slice(0, 10).split("-").reverse().join(".");

export default function PetitionsPage() {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState([]);
  const [email, setEmail] = useState("");
  const [ptype, setPtype] = useState("initiation");
  const [busy, setBusy] = useState(false);
  const [genFor, setGenFor] = useState(null);
  const [genLang, setGenLang] = useState(normalizeLangCode(i18n.language || "ro"));
  const [prop, setProp] = useState({ p1n: "", p1k: "", p2n: "", p2k: "" });
  const [genBusy, setGenBusy] = useState(false);
  const [searchInit, setSearchInit] = useState("");
  const [searchAff, setSearchAff] = useState("");
  const [schedFor, setSchedFor] = useState(null);
  const [sched, setSched] = useState({ date: "", time: "", location: "", message: "" });
  const [schedBusy, setSchedBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/petitions");
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => { load(); }, []);

  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/petitions", { email: email.trim(), ptype });
      if (data.email_sent) {
        toast.success(t("petitions.sentOk", "Link-ul petiției a fost trimis pe email."));
      } else {
        try { await navigator.clipboard.writeText(data.link); } catch (_e) {}
        toast.warning(t("petitions.sentFallback", "Emailul nu a putut fi livrat. Link-ul a fost copiat — trimite-l manual: ") + data.link, { duration: 12000 });
      }
      setEmail("");
      load();
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  const generate = async () => {
    if (!genFor) return;
    setGenBusy(true);
    try {
      const resp = await api.post(`/petitions/${genFor.id}/generate`, {
        lang: genLang,
        proposer1_name: prop.p1n, proposer1_known: prop.p1k,
        proposer2_name: prop.p2n, proposer2_known: prop.p2k,
      }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `petitie-${(genFor.data?.full_name || genFor.email).replace(/\s+/g, "_")}-${genLang}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      setGenFor(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGenBusy(false);
    }
  };

  const downloadCv = async (rec) => {
    try {
      const resp = await api.get(`/petitions/${rec.id}/cv`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url; a.download = rec.cv_filename || "cv.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const scheduleSend = async () => {
    if (!schedFor || !sched.date || !sched.time || !sched.location.trim()) return;
    setSchedBusy(true);
    try {
      await api.post(`/petitions/${schedFor.id}/schedule`, sched);
      toast.success(t("petitions.schedOk", "Convocarea a fost trimisă candidatului pe email."));
      setSchedFor(null);
      setSched({ date: "", time: "", location: "", message: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSchedBusy(false);
    }
  };

  const remove = async (rec) => {
    if (!window.confirm(t("petitions.confirmDelete", "Ștergi această petiție?"))) return;
    try {
      await api.delete(`/petitions/${rec.id}`);
      setItems((prev) => prev.filter((x) => x.id !== rec.id));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const statusLabel = (s) => t(`petitions.status_${s}`, { sent: "Trimisă", submitted: "În așteptare", generated: "Generată" }[s] || s);

  const matches = (rec, q) => {
    if (!q.trim()) return true;
    const s = q.trim().toLowerCase();
    return [rec.data?.full_name, rec.email, rec.data?.mother_lodge, rec.data?.jurisdiction, rec.data?.occupation]
      .some((v) => (v || "").toLowerCase().includes(s));
  };

  const initItems = useMemo(() => items.filter((r) => (r.ptype || "initiation") !== "affiliation" && matches(r, searchInit)), [items, searchInit]);
  const affItems = useMemo(() => items.filter((r) => r.ptype === "affiliation" && matches(r, searchAff)), [items, searchAff]);

  const statusCell = (rec) => (
    <>
      <span style={{ color: STATUS_COLORS[rec.status] || "#333", fontWeight: 700, fontSize: "0.8rem", letterSpacing: "0.06em" }}
        data-testid={`petition-status-${rec.email}`}>
        {statusLabel(rec.status).toUpperCase()}
      </span>
      {rec.ceremony_date && (
        <div style={{ color: "#0d7a3c", fontSize: "0.72rem", fontWeight: 600, marginTop: 2, whiteSpace: "nowrap" }}
          data-testid={`petition-scheduled-${rec.email}`}>
          <CalendarClock size={11} style={{ display: "inline", verticalAlign: "-1px", marginRight: 3 }} />
          {t("petitions.scheduledFor", "Programat pentru")} {(rec.ceremony_date || "").split("-").reverse().join(".")} {rec.ceremony_time || ""}
        </div>
      )}
    </>
  );

  const actionsCell = (rec) => (
    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
      {rec.cv_path && (
        <button className="btn-outline-gold" style={{ padding: "4px 10px", marginRight: 6 }}
          title={t("petitions.cv", "CV")} onClick={() => downloadCv(rec)} data-testid={`petition-cv-${rec.email}`}>
          <Paperclip size={13} />
        </button>
      )}
      {(rec.status === "submitted" || rec.status === "generated") && (
        <button className="btn-primary-gold" style={{ padding: "4px 12px", marginRight: 6 }}
          onClick={() => { setGenFor(rec); setProp({ p1n: rec.proposer1_name || "", p1k: rec.proposer1_known || "", p2n: rec.proposer2_name || "", p2k: rec.proposer2_known || "" }); }}
          data-testid={`petition-generate-${rec.email}`}>
          <FileText size={13} style={{ marginRight: 4 }} /> PDF
        </button>
      )}
      {(rec.status === "submitted" || rec.status === "generated") && (
        <button className="btn-outline-gold" style={{ padding: "4px 12px", marginRight: 6 }}
          onClick={() => { setSchedFor(rec); setSched({ date: rec.ceremony_date || "", time: rec.ceremony_time || "", location: rec.ceremony_location || "", message: "" }); }}
          data-testid={`petition-schedule-${rec.email}`}>
          <CalendarClock size={13} style={{ marginRight: 4 }} />
          {rec.ptype === "affiliation" ? t("petitions.scheduleAff", "Programează afilierea") : t("petitions.scheduleInit", "Programează inițierea")}
        </button>
      )}
      <button className="btn-outline-gold" style={{ padding: "4px 10px", color: "#a33" }}
        onClick={() => remove(rec)} data-testid={`petition-delete-${rec.email}`}>
        <Trash2 size={13} />
      </button>
    </td>
  );

  const searchBox = (value, setValue, testId) => (
    <div style={{ position: "relative", maxWidth: 320, marginBottom: 12 }}>
      <Search size={14} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#9a7530" }} />
      <input className="input-lodge" style={{ paddingLeft: 32 }} value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={t("petitions.search", "Caută după nume, email...")}
        data-testid={testId} />
    </div>
  );

  const stickyTh = { position: "sticky", top: 0, background: "#faf6ec", zIndex: 1 };

  return (
    <div data-testid="petitions-page">
      <div className="page-header" style={{ marginBottom: "1.5rem" }}>
        <h1 className="page-title">{t("petitions.title", "Petiții")}</h1>
        <p className="page-subtitle">{t("petitions.subtitle", "Trimite profanului link-ul de completare — el alege limba, răspunde la întrebări și semnează.")}</p>
      </div>

      <div className="gold-card" style={{ padding: "1.5rem", marginBottom: "1.5rem" }}>
        <form onSubmit={send} className="flex flex-wrap items-end gap-4">
          <div style={{ minWidth: 220 }}>
            <label className="label-lodge">{t("petitions.typeLabel", "Tipul petiției")}</label>
            <select className="input-lodge" value={ptype} onChange={(e) => setPtype(e.target.value)} data-testid="petition-type">
              <option value="initiation">{t("petitions.typeInitiation", "Inițiere (profan)")}</option>
              <option value="affiliation">{t("petitions.typeAffiliation", "Afiliere (mason)")}</option>
            </select>
          </div>
          <div style={{ flex: 1, minWidth: 260 }}>
            <label className="label-lodge">{t("petitions.emailLabel", "Emailul destinatarului")}</label>
            <input type="email" className="input-lodge" required value={email}
              onChange={(e) => setEmail(e.target.value)} placeholder="destinatar@email.com"
              data-testid="petition-email" />
          </div>
          <button type="submit" className="btn-primary-gold flex items-center gap-2" disabled={busy} data-testid="petition-send-btn">
            <Send size={14} /> {busy ? "..." : t("petitions.send", "Trimite link-ul")}
          </button>
        </form>
      </div>

      {/* ——— Petiții de inițiere ——— */}
      <div className="gold-card" style={{ padding: "1.5rem", marginBottom: "1.5rem" }} data-testid="initiation-section">
        <h2 className="kv-label" style={{ fontSize: "0.75rem", marginBottom: 10 }}>
          {t("petitions.historyInit", "Petiții de inițiere")} ({initItems.length})
        </h2>
        {searchBox(searchInit, setSearchInit, "initiation-search")}
        {initItems.length === 0 ? (
          <p className="page-subtitle">{searchInit.trim() ? t("petitions.noResults", "Nicio petiție găsită.") : t("petitions.empty", "Nicio petiție trimisă încă.")}</p>
        ) : (
          <div style={{ overflowX: "auto", overflowY: "auto", maxHeight: 420 }}>
            <table className="lodge-table" data-testid="initiation-table">
              <thead>
                <tr>
                  <th style={stickyTh}>{t("petitions.colName", "Petent")}</th>
                  <th style={stickyTh}>Email</th>
                  <th style={stickyTh}>{t("petitions.colStatus", "Status")}</th>
                  <th style={stickyTh}>{t("petitions.colDate", "Data")}</th>
                  <th style={{ ...stickyTh, textAlign: "right" }}></th>
                </tr>
              </thead>
              <tbody>
                {initItems.map((rec) => (
                  <tr key={rec.id}>
                    <td style={{ fontWeight: 600 }}>{rec.data?.full_name || "—"}</td>
                    <td>{rec.email}</td>
                    <td>{statusCell(rec)}</td>
                    <td>{fmtDate(rec)}</td>
                    {actionsCell(rec)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ——— Petiții de afiliere ——— */}
      <div className="gold-card" style={{ padding: "1.5rem" }} data-testid="affiliation-section">
        <h2 className="kv-label" style={{ fontSize: "0.75rem", marginBottom: 10 }}>
          {t("petitions.historyAff", "Petiții de afiliere")} ({affItems.length})
        </h2>
        {searchBox(searchAff, setSearchAff, "affiliation-search")}
        {affItems.length === 0 ? (
          <p className="page-subtitle">{searchAff.trim() ? t("petitions.noResults", "Nicio petiție găsită.") : t("petitions.empty", "Nicio petiție trimisă încă.")}</p>
        ) : (
          <div style={{ overflowX: "auto", overflowY: "auto", maxHeight: 420 }}>
            <table className="lodge-table" data-testid="affiliation-table">
              <thead>
                <tr>
                  <th style={stickyTh}>{t("petitions.colName", "Petent")}</th>
                  <th style={stickyTh}>Email</th>
                  <th style={stickyTh}>{t("petitions.colMotherLodge", "Loja de proveniență")}</th>
                  <th style={stickyTh}>{t("petitions.colJurisdiction", "Jurisdicția")}</th>
                  <th style={stickyTh}>{t("petitions.colDegree", "Grad")}</th>
                  <th style={stickyTh}>{t("petitions.colStatus", "Status")}</th>
                  <th style={stickyTh}>{t("petitions.colDate", "Data")}</th>
                  <th style={{ ...stickyTh, textAlign: "right" }}></th>
                </tr>
              </thead>
              <tbody>
                {affItems.map((rec) => (
                  <tr key={rec.id}>
                    <td style={{ fontWeight: 600 }}>{rec.data?.full_name || "—"}</td>
                    <td>{rec.email}</td>
                    <td>{rec.data?.mother_lodge || "—"}</td>
                    <td>{rec.data?.jurisdiction || "—"}</td>
                    <td>{rec.data?.degree || "—"}</td>
                    <td>{statusCell(rec)}</td>
                    <td>{fmtDate(rec)}</td>
                    {actionsCell(rec)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {genFor && (
        <div className="modal-overlay" onClick={() => setGenFor(null)}>
          <div className="modal-content gold-card" style={{ maxWidth: 560, padding: "1.4rem" }} onClick={(e) => e.stopPropagation()} data-testid="petition-generate-modal">
            <div className="flex items-center justify-between mb-3">
              <h3 style={{ fontFamily: "Cinzel, serif", letterSpacing: "0.14em", color: "#0d1b2a" }}>
                {t("petitions.generateTitle", "Generează petiția PDF")}
              </h3>
              <button onClick={() => setGenFor(null)} className="text-gray-500"><X size={18} /></button>
            </div>
            <p className="kv-label" style={{ fontSize: "0.72rem", marginBottom: 10 }}>
              {genFor.data?.full_name} — {t("petitions.completedIn", "completată în")} {(LANG_OPTS.find(([c]) => c === genFor.lang) || [])[1] || genFor.lang}
            </p>
            <label className="label-lodge">{t("petitions.genLang", "Limba de generare")}</label>
            <select className="input-lodge" value={genLang} onChange={(e) => setGenLang(e.target.value)} data-testid="petition-gen-lang">
              {LANG_OPTS.map(([c, n]) => <option key={c} value={c}>{n}</option>)}
            </select>
            <div className="grid grid-cols-2 gap-3" style={{ marginTop: 12 }}>
              <div>
                <label className="label-lodge">{t("petitions.proposer1", "Primul propunător")}</label>
                <input className="input-lodge" value={prop.p1n} onChange={(e) => setProp({ ...prop, p1n: e.target.value })} data-testid="petition-p1-name" />
              </div>
              <div>
                <label className="label-lodge">{t("petitions.known", "Îl cunoaște de")}</label>
                <input className="input-lodge" placeholder="ex: 5 ani" value={prop.p1k} onChange={(e) => setProp({ ...prop, p1k: e.target.value })} data-testid="petition-p1-known" />
              </div>
              <div>
                <label className="label-lodge">{t("petitions.proposer2", "Al doilea propunător")}</label>
                <input className="input-lodge" value={prop.p2n} onChange={(e) => setProp({ ...prop, p2n: e.target.value })} />
              </div>
              <div>
                <label className="label-lodge">{t("petitions.known", "Îl cunoaște de")}</label>
                <input className="input-lodge" value={prop.p2k} onChange={(e) => setProp({ ...prop, p2k: e.target.value })} />
              </div>
            </div>
            <div className="gold-divider my-4" />
            <button className="btn-primary-gold flex items-center gap-2" onClick={generate} disabled={genBusy} data-testid="petition-gen-confirm">
              <Download size={14} /> {genBusy ? "..." : t("petitions.generate", "Generează & Descarcă")}
            </button>
          </div>
        </div>
      )}

      {schedFor && (
        <div className="modal-overlay" onClick={() => setSchedFor(null)}>
          <div className="modal-content gold-card" style={{ maxWidth: 520, padding: "1.4rem" }} onClick={(e) => e.stopPropagation()} data-testid="petition-schedule-modal">
            <div className="flex items-center justify-between mb-3">
              <h3 style={{ fontFamily: "Cinzel, serif", letterSpacing: "0.14em", color: "#0d1b2a" }}>
                {schedFor.ptype === "affiliation" ? t("petitions.scheduleAff", "Programează afilierea") : t("petitions.scheduleInit", "Programează inițierea")}
              </h3>
              <button onClick={() => setSchedFor(null)} className="text-gray-500" data-testid="petition-schedule-close"><X size={18} /></button>
            </div>
            <p className="kv-label" style={{ fontSize: "0.72rem", marginBottom: 10 }}>
              {schedFor.data?.full_name || schedFor.email} — {t("petitions.completedIn", "completată în")} {(LANG_OPTS.find(([c]) => c === schedFor.lang) || [])[1] || schedFor.lang}
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-lodge">{t("petitions.schedDate", "Data ceremoniei")} *</label>
                <input type="date" className="input-lodge" value={sched.date}
                  onChange={(e) => setSched({ ...sched, date: e.target.value })} data-testid="petition-sched-date" />
              </div>
              <div>
                <label className="label-lodge">{t("petitions.schedTime", "Ora")} *</label>
                <input type="time" className="input-lodge" value={sched.time}
                  onChange={(e) => setSched({ ...sched, time: e.target.value })} data-testid="petition-sched-time" />
              </div>
            </div>
            <div style={{ marginTop: 12 }}>
              <label className="label-lodge">{t("petitions.schedLocation", "Locația")} *</label>
              <input className="input-lodge" value={sched.location}
                onChange={(e) => setSched({ ...sched, location: e.target.value })} data-testid="petition-sched-location" />
            </div>
            <div style={{ marginTop: 12 }}>
              <label className="label-lodge">{t("petitions.schedMessage", "Mesaj personal (opțional — va fi tradus automat în limba candidatului)")}</label>
              <textarea className="input-lodge" rows={3} value={sched.message}
                onChange={(e) => setSched({ ...sched, message: e.target.value })} data-testid="petition-sched-message" />
            </div>
            <div className="gold-divider my-4" />
            <button className="btn-primary-gold flex items-center gap-2" onClick={scheduleSend}
              disabled={schedBusy || !sched.date || !sched.time || !sched.location.trim()}
              data-testid="petition-sched-confirm">
              <Send size={14} /> {schedBusy ? "..." : t("petitions.schedSend", "Trimite convocarea")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
